import viatorLogo from "@/assets/svg/viator-logo.svg";
import type { TFunction } from "i18next";
import type { PartnerSettings } from "@/types/partner-settings";
import type {
  GuesteriaPartnerCategoryId,
  GuesteriaPartnerDetail,
  GuesteriaPartnerRow,
} from "./guesteriaPartnersModel";

function translateProviderName(provider: string, t: TFunction): string {
  const key = provider.toUpperCase();
  return t(`partners.providers.${key}`, {
    defaultValue: provider.replace(/_/g, " "),
  });
}

function destinationTypeLabels(
  t: TFunction,
  raw: string | undefined,
): string[] {
  if (!raw?.trim()) {
    return [];
  }
  const normalized = raw.trim();
  const slug = normalized
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .join("_");
  const i18nKey = slug
    ? `partners.destinationTypes.${slug}`
    : "partners.destinationTypes.unknown";
  return [t(i18nKey, { defaultValue: normalized })];
}

function destinationTypeToCategory(
  type: string | undefined,
): GuesteriaPartnerCategoryId {
  if (!type) return "tour";
  const lower = type.toLowerCase();
  if (lower.includes("museum")) return "museum";
  if (lower.includes("trip")) return "trip";
  if (lower.includes("art")) return "art";
  return "tour";
}

function logoForProvider(provider: string): GuesteriaPartnerRow["logo"] {
  const p = provider.toUpperCase();
  if (p === "VIATOR") {
    return { letter: "V", className: "bg-emerald-600", imageSrc: viatorLogo };
  }
  const letter = provider.charAt(0).toUpperCase() || "?";
  return { letter, className: "bg-slate-600" };
}

export function providerSlugFromSettings(provider: string): string {
  return provider.toLowerCase();
}

export function mapPartnerSettingsToRow(
  settings: PartnerSettings,
  t: TFunction,
  flagEmoji?: string | null,
): GuesteriaPartnerRow {
  const provider = String(settings.provider);
  const id = providerSlugFromSettings(provider);
  const name = translateProviderName(provider, t);

  const city = (
    settings.destination?.destinationName ?? settings.destinationName
  )?.trim();
  const resolvedFlagEmoji = settings.destination?.flagEmoji ?? flagEmoji ?? "";
  const region =
    city !== undefined && city.length > 0
      ? { flagEmoji: resolvedFlagEmoji, city }
      : undefined;

  return {
    id,
    enabled: settings.enabled,
    name,
    logo: logoForProvider(provider),
    category: destinationTypeToCategory(settings.destinationType),
    connectionStatus: settings.enabled ? "installed" : "unavailable",
    region,
    commissionPercent:
      settings.hotelFeePercent != null ? settings.hotelFeePercent : undefined,
    revenueEur:
      settings.lastMonthRevenue != null ? settings.lastMonthRevenue : undefined,
  };
}

export function mapPartnerSettingsToDetail(
  settings: PartnerSettings,
  t: TFunction,
  flagEmoji?: string | null,
): GuesteriaPartnerDetail {
  const row = mapPartnerSettingsToRow(settings, t, flagEmoji);
  const provider = String(settings.provider);
  const descKey = `partners.detail.providerDescriptions.${provider}`;
  const translated = t(descKey, { defaultValue: "" });
  const description = translated
    ? translated
    : t("partners.detail.descriptionFallback");

  const categoryLabels = destinationTypeLabels(t, settings.destinationType);

  return {
    ...row,
    description,
    categoryLabels,
  };
}
